import { useForm } from "react-hook-form";
import AuthCodeApi from "../../api/auth-code";
import Auth from "../../api/auth";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthContext from "../../components/auth-context";
import Alert from "../../components/login/alert";
import FileApi from "../../api/file";
import PurchaseTicketApi from "../../api/purchase-ticket";
import ShopLayout from "../../components/signup/shop-layout";
import TagsApi from "../../api/tags";
import SendUserPasswordApi from "../../api/send-user-password";

interface IVerifyFormData {
  code: string;
}

export default function VerifyNumber() {
  const { register, handleSubmit, formState } = useForm<IVerifyFormData>();
  const { errors } = formState;
  const context = useContext(AuthContext);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { t } = useTranslation(undefined, { keyPrefix: "SignUp.VerifyNumber" });
  const navigate = useNavigate();
  const location = useLocation();

  const {
    cellPhone,
    type,
    tariffId,
    ngnNumber,
    shippingId,
    optionalProducts,
    isShopPendingCustomer,
    tags,
    handleAlert = () => {},
    alert = false,
  } = location.state || {};

  async function onFormSubmit(data: IVerifyFormData) {
    setErrorMessage("");

    if (!cellPhone || !type || !tariffId) {
      setErrorMessage("اطلاعات ناقص است. لطفاً مجدد اقدام کنید.");
      return;
    }

    try {
      const verifyRes = await new AuthCodeApi().put({
        username: cellPhone,
        code: data.code,
      });
      if (verifyRes.errorMessage) {
        setErrorMessage(verifyRes.errorMessage);
        return;
      }

      const authData = await Auth.login({
        username: cellPhone,
        password: data.code,
        serviceType: "0",
      });
      context.login(authData.LoginId);
      context.authId = authData.LoginId;
      context.serviceType = 0;

      let requestUniqueIdLocal: string | undefined;
      if (tags && tags.length > 0) {
        const allTags = tags
          .split(",")
          .map((tag: string) => tag.trim())
          .filter(Boolean);

        if (allTags.length > 0) {
          const tagsRes = await new TagsApi(context).post(allTags);
          if (tagsRes.errorMessage) {
            setErrorMessage(tagsRes.errorMessage);
            return;
          }
          requestUniqueIdLocal = tagsRes.id;
          console.log("Tags created with id:", requestUniqueIdLocal);
        }
      }

      const phone = type === 40 ? ngnNumber : "";
      const fileApi = new FileApi(context);
      const postRes = await fileApi.post(type, phone, "", "", "Owner", "", "");

      if (postRes.errorMessage) {
        setErrorMessage(postRes.errorMessage);
        return;
      }
      context.setActiveFileId(postRes.id);

      const mainProduct = Number(tariffId);
      const optionalProductIds = Array.isArray(optionalProducts)
        ? optionalProducts
        : [];

      const ticket = await new PurchaseTicketApi(context).post(
        postRes.id,
        0,
        mainProduct,
        optionalProductIds,
        shippingId
      );

      await new PurchaseTicketApi(context).getAll().then(context.setTickets);

      console.log("Navigating with requestUniqueId:", requestUniqueIdLocal);
      navigate(`/shop/payment/${ticket.id}`, {
        state: {
          type,
          tariffId,
          ticketId: ticket.id,
          cellPhone,
          optionalProducts,
          isShopPendingCustomer,
          requestUniqueId: requestUniqueIdLocal,
        },
      });
    } catch (err) {
      console.error("Submission error:", err);
      setErrorMessage("خطا در تأیید کد. لطفاً دوباره تلاش کنید.");
    }
  }

  function handleForgot() {
    const api = new AuthCodeApi();
    api.post({ username: cellPhone, code: "0" }).then(() => {
      handleAlert(true);
    });
  }

  return (
    <ShopLayout
      type={type}
      step={type === 40 ? 4 : 3}
      steps={type === 40 ? [3, 4, 5] : [2, 3, 4]}
    >
      <form onSubmit={handleSubmit(onFormSubmit)} className="signup-section">
        <div className="xs:w-full md:w-2/4 xl:w-2/6 mx-auto xs:p-0 mt-10 p-10 rounded-xl">
          <div className="signup-form-head-section mb-2 flex-col flex">
            <span className="sepanta-icon-mobile icon-box"></span>
            <h3 className="head-title pt-4">{t("Cell Number Confirmation")}</h3>
          </div>

          {alert && <Alert color="green" message={t("Resend code alert")} />}

          <div className="form-section rtl flex flex-grow flex-col gap-4 mb-0">
            <div className="form-group flex flex-col w-full rtl text-center mt-2 relative">
              <label className="pr-4 mb-10">
                {t("enter the code", { cellPhone })}
              </label>
              <input
                {...register("code", { required: t("Code is required") })}
                type="tel"
                className="border-2 p-4 rounded-lg mt-2 text-center verify-code-input"
                placeholder="- - - - -"
              />
              <p className="absolute left-0 right-0 top-8 text-red-500">
                {errors.code?.message || errorMessage}
              </p>
            </div>
          </div>

          <div className="form-section rtl flex flex-col mt-6 gap-4 justify-between pb-10">
            <button
              type="submit"
              className="verify-account-btn p-5 flex text-center justify-center"
            >
              {t("Submit")}
              <span className="sepanta-icon-left-long-arrow"></span>
            </button>

            <button
              type="button"
              className="flex items-center justify-center navigate-link-bd"
              onClick={handleForgot}
            >
              {t("Resend the code")}
            </button>
          </div>
        </div>
      </form>
    </ShopLayout>
  );
}
